import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Flex,
  Grid,
  Group,
  Image,
  Loader,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconCircle,
  IconDots,
  IconFilter,
  IconPlus,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { IMAGE_ASSET_TYPES, TEXT_ASSET_TYPES } from '../../../constants/googleAssetTypes';
import {
  ASPECT_RATIO_TOLERANCE,
  validateAssetAspectRatio,
  validateAssetTextLength,
  withImageSpecLabels,
  withTextLengthLabels,
} from '../../../helpers/googleAssetValidation';
import useFetch from '../../../helpers/useFetch';
import ErrorMessage from '../../ErrorMessage';
import GoogleAddAssetsModal from '../../Modals/GoogleAddAssetsModal';
import Spinner from '../../Spinner';

const IMAGE_TYPE_OPTIONS = withImageSpecLabels(IMAGE_ASSET_TYPES);
const TEXT_TYPE_OPTIONS = withTextLengthLabels(TEXT_ASSET_TYPES);

const INITIAL_LIMIT = 20;
const LIMIT_INCREMENT = 20;

const FILTER_TYPES = [
  { value: 'contains', label: 'Contains' },
  { value: 'notContains', label: 'Does Not Contain' },
  { value: 'equals', label: 'Equals' },
];

const GoogleAssetSelector = ({ selectedAssets = [], handleAssetSelect, onValidationChange }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('text');
  const [textAssets, setTextAssets] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLimitAlert, setShowLimitAlert] = useState(null);
  const [error, setError] = useState(undefined);
  const [assetsLoaded, setAssetsLoaded] = useState({ text: false, images: false });
  const [textLimit, setTextLimit] = useState(INITIAL_LIMIT);
  const [imageLimit, setImageLimit] = useState(INITIAL_LIMIT);
  const [hasMore, setHasMore] = useState({ text: true, images: true });
  const [filterName, setFilterName] = useState('');
  const [filterType, setFilterType] = useState('contains');
  const [addAssetModalType, setAddAssetModalType] = useState(null);
  const fetch = useFetch();
  const { accountId, domainId } = useParams();

  const fetchAssets = useCallback(
    async (type, limit, filterParams = {}) => {
      setLoading(true);
      setError(undefined);
      try {
        const apiType = type === 'text' ? 'TEXT' : 'IMAGE';
        let url = `/api/admin/accounts/${accountId}/domains/${domainId}/google/assets/${apiType}?sizeLimit=${limit}`;

        if (filterParams.filterName && filterParams.filterType) {
          url += `&filterName=${encodeURIComponent(filterParams.filterName)}&filterType=${filterParams.filterType}`;
        }

        const response = await fetch(url);
        const newAssets = response.assets || [];

        if (type === 'text') {
          setTextAssets(newAssets);
          setAssetsLoaded((prev) => ({ ...prev, text: true }));
          setHasMore((prev) => ({ ...prev, text: newAssets.length === limit }));
        } else {
          setImages(newAssets);
          setAssetsLoaded((prev) => ({ ...prev, images: true }));
          setHasMore((prev) => ({ ...prev, images: newAssets.length === limit }));
        }
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    [accountId, domainId, fetch]
  );

  const handleApplyFilter = () => {
    const type = activeTab;
    const limit = type === 'text' ? textLimit : imageLimit;

    if (filterName.trim() === '') {
      fetchAssets(type, limit);
    } else {
      fetchAssets(type, limit, { filterName, filterType });
    }
  };

  const loadMoreAssets = useCallback(
    (type) => {
      const filterParams = filterName.trim() !== '' ? { filterName, filterType } : {};

      if (type === 'text') {
        const newLimit = textLimit + LIMIT_INCREMENT;
        setTextLimit(newLimit);
        fetchAssets(type, newLimit, filterParams);
      } else {
        const newLimit = imageLimit + LIMIT_INCREMENT;
        setImageLimit(newLimit);
        fetchAssets(type, newLimit, filterParams);
      }
    },
    [fetchAssets, textLimit, imageLimit, filterName, filterType]
  );

  const toggleOpen = () => setIsOpen(!isOpen);

  const countAssetsByType = (assets, type) => {
    return assets.filter((asset) => asset.fieldType === type).length;
  };

  const canAddAsset = (fieldType) => {
    const typeConfig = [...TEXT_ASSET_TYPES, ...IMAGE_ASSET_TYPES].find((t) => t.value === fieldType);
    if (!typeConfig) return false;
    return countAssetsByType(selectedAssets, fieldType) < typeConfig.maxCount;
  };

  const ratioValidationByAsset = useMemo(() => {
    const map = new Map();
    selectedAssets.forEach((asset) => {
      if (asset.type === 'IMAGE') {
        map.set(asset.resourceName, validateAssetAspectRatio(asset, asset.fieldType));
      }
    });
    return map;
  }, [selectedAssets]);

  const textValidationByAsset = useMemo(() => {
    const map = new Map();
    selectedAssets.forEach((asset) => {
      if (asset.type === 'TEXT') {
        map.set(asset.resourceName, validateAssetTextLength(asset, asset.fieldType));
      }
    });
    return map;
  }, [selectedAssets]);

  const ratioErrors = useMemo(
    () =>
      selectedAssets
        .filter((a) => a.type === 'IMAGE')
        .map((asset) => ({ asset, validation: ratioValidationByAsset.get(asset.resourceName) }))
        .filter(({ validation }) => validation && validation.valid === false),
    [selectedAssets, ratioValidationByAsset]
  );

  const textErrors = useMemo(
    () =>
      selectedAssets
        .filter((a) => a.type === 'TEXT')
        .map((asset) => ({ asset, validation: textValidationByAsset.get(asset.resourceName) }))
        .filter(({ validation }) => validation && validation.valid === false),
    [selectedAssets, textValidationByAsset]
  );

  const requirementRows = useMemo(() => {
    const rows = [];
    [...TEXT_ASSET_TYPES, ...IMAGE_ASSET_TYPES].forEach((type) => {
      const isImage = Boolean(type.aspectRatio);
      const count = countAssetsByType(selectedAssets, type.value);
      const hasError = isImage
        ? ratioErrors.some(({ asset }) => asset.fieldType === type.value)
        : textErrors.some(({ asset }) => asset.fieldType === type.value);
      let status;
      if (hasError) status = 'error';
      else if (count >= type.minCount) status = 'ok';
      else status = 'pending';

      rows.push({
        kind: isImage ? 'IMAGE' : 'TEXT',
        value: type.value,
        label: type.label,
        minCount: type.minCount,
        maxCount: type.maxCount,
        maxLength: type.maxLength,
        count,
        status,
        aspectRatioLabel: type.aspectRatioLabel,
        recommendedDimensions: type.recommendedDimensions,
      });
    });
    return rows;
  }, [selectedAssets, ratioErrors, textErrors]);

  const hasMinCountIssue = useMemo(() => requirementRows.some((row) => row.count < row.minCount), [requirementRows]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(hasMinCountIssue || ratioErrors.length > 0 || textErrors.length > 0);
    }
  }, [onValidationChange, hasMinCountIssue, ratioErrors, textErrors]);

  const getDefaultFieldType = (asset) => {
    if (asset.type === 'TEXT') {
      const length = asset.text.length;

      // BUSINESS_NAME has maxCount 1 and is rarely the right pick; users assign it manually.
      // 60 is a heuristic split between DESCRIPTION and LONG_HEADLINE — both allow up to 90,
      // but copy >60 chars usually reads as a long headline rather than a product description.
      if (length <= 30) return 'HEADLINE';
      if (length <= 60) return 'DESCRIPTION';

      // No valid default for length > 90: LONG_HEADLINE/DESCRIPTION both cap at 90, so
      // the validator will flag it and the user must shorten the text or split it.
      return 'LONG_HEADLINE';
    }

    if (asset.type === 'IMAGE') {
      const { width, height } = asset;
      if (!width || !height) return 'MARKETING_IMAGE';
      const aspectRatio = width / height;

      if (Math.abs(aspectRatio - 1.91) <= 1.91 * ASPECT_RATIO_TOLERANCE) return 'MARKETING_IMAGE';
      if (Math.abs(aspectRatio - 1) <= 1 * ASPECT_RATIO_TOLERANCE) {
        // Logos are typically small icons; images larger than 512px on a side are more likely square marketing images.
        return width <= 512 && height <= 512 ? 'LOGO' : 'SQUARE_MARKETING_IMAGE';
      }

      return 'MARKETING_IMAGE';
    }

    return 'HEADLINE';
  };

  const handleAssetToggle = (asset, fieldType) => {
    const exists = selectedAssets.find((selected) => selected.resourceName === asset.resourceName);

    if (exists) {
      setShowLimitAlert(null);
      handleAssetSelect(selectedAssets.filter((selected) => selected.resourceName !== asset.resourceName));
      return;
    }

    const resolvedFieldType =
      fieldType || (activeTab === 'text' ? TEXT_ASSET_TYPES[0].value : IMAGE_ASSET_TYPES[0].value);

    if (!canAddAsset(resolvedFieldType)) {
      const typeConfig = [...TEXT_ASSET_TYPES, ...IMAGE_ASSET_TYPES].find((t) => t.value === resolvedFieldType);
      setShowLimitAlert(`Maximum limit of ${resolvedFieldType} (${typeConfig.maxCount}) reached.`);
      return;
    }

    setShowLimitAlert(null);
    handleAssetSelect([...selectedAssets, { ...asset, fieldType: resolvedFieldType }]);
  };

  const handleFieldTypeChange = (asset, fieldType) => {
    const exists = selectedAssets.find((a) => a.resourceName === asset.resourceName);

    if (exists) {
      if (exists.fieldType !== fieldType && !canAddAsset(fieldType)) {
        const typeConfig = [...TEXT_ASSET_TYPES, ...IMAGE_ASSET_TYPES].find((t) => t.value === fieldType);
        setShowLimitAlert(`Maximum limit of ${fieldType} (${typeConfig.maxCount}) reached.`);
        return;
      }
      setShowLimitAlert(null);
      handleAssetSelect(
        selectedAssets.map((selected) =>
          selected.resourceName === asset.resourceName ? { ...selected, fieldType } : selected
        )
      );
      return;
    }

    if (!canAddAsset(fieldType)) {
      const typeConfig = [...TEXT_ASSET_TYPES, ...IMAGE_ASSET_TYPES].find((t) => t.value === fieldType);
      setShowLimitAlert(`Maximum limit of ${fieldType} (${typeConfig.maxCount}) reached.`);
      return;
    }

    setShowLimitAlert(null);
    handleAssetSelect([...selectedAssets, { ...asset, fieldType }]);
  };

  const loadAssets = (type) => {
    setActiveTab(type);
    setTextLimit(INITIAL_LIMIT);
    setImageLimit(INITIAL_LIMIT);
    setHasMore({ text: true, images: true });
    fetchAssets(type, INITIAL_LIMIT);
  };

  const renderRequirementsChecklist = () => (
    <Stack gap={6} mb="md">
      <Text fw={500} size="sm">
        Requirements
      </Text>
      {requirementRows.map((row) => {
        let StatusIcon = IconCircle;
        let iconColor = 'var(--mantine-color-gray-5)';
        let textColor;
        if (row.status === 'ok') {
          StatusIcon = IconCheck;
          iconColor = 'var(--mantine-color-green-6)';
          textColor = 'green';
        } else if (row.status === 'error') {
          StatusIcon = IconAlertTriangle;
          iconColor = 'var(--mantine-color-red-6)';
          textColor = 'red';
        }

        return (
          <Group key={row.value} justify="space-between" wrap="nowrap" gap="sm">
            <Group gap={6} wrap="nowrap">
              <StatusIcon size={14} color={iconColor} />
              <Text size="sm" c={textColor}>
                {row.label}
              </Text>
            </Group>
            <Group gap="md" wrap="nowrap">
              {row.kind === 'IMAGE' && row.aspectRatioLabel && (
                <Text size="xs" c="dimmed">
                  {row.aspectRatioLabel} ({row.recommendedDimensions})
                </Text>
              )}
              {row.kind === 'TEXT' && row.maxLength && (
                <Text size="xs" c="dimmed">
                  max {row.maxLength} chars
                </Text>
              )}
              <Text size="sm" c={textColor || 'dimmed'}>
                {row.count} / {row.minCount}–{row.maxCount}
              </Text>
            </Group>
          </Group>
        );
      })}
    </Stack>
  );

  const renderSelectedAssets = () => {
    const selectedTextAssets = selectedAssets.filter((asset) => asset.type === 'TEXT');
    const selectedImageAssets = selectedAssets.filter((asset) => asset.type === 'IMAGE');

    return (
      <>
        {renderRequirementsChecklist()}
        {showLimitAlert && (
          <Alert color="yellow" mb="md">
            {showLimitAlert}
          </Alert>
        )}
        {selectedAssets.length === 0 ? (
          <Text c="dimmed" mb="sm">
            No assets selected yet.
          </Text>
        ) : null}
        <Title order={5}>Selected Assets</Title>

        <Tabs value={activeTab} onChange={setActiveTab} mb="md">
          <Tabs.List>
            <Tabs.Tab value="text">{`Text Assets (${selectedTextAssets.length})`}</Tabs.Tab>
            <Tabs.Tab value="images">{`Image Assets (${selectedImageAssets.length})`}</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="text" p="md">
            {selectedTextAssets.length === 0 ? (
              <Text c="dimmed">No text assets selected</Text>
            ) : (
              <Grid gutter="lg" mt="md">
                {selectedTextAssets.map((asset) => {
                  const validation = textValidationByAsset.get(asset.resourceName);
                  const invalid = validation && validation.valid === false;
                  const suggested = invalid ? validation.suggestedFieldType : null;
                  const suggestedConfig = suggested ? TEXT_ASSET_TYPES.find((t) => t.value === suggested) : null;
                  const canSwitch = suggested && canAddAsset(suggested);
                  const cardShadow = invalid
                    ? '0 0 0 2px var(--mantine-color-red-6)'
                    : '0 0 0 2px var(--mantine-primary-color-filled)';
                  return (
                    <Grid.Col key={asset.resourceName} span={{ base: 12, md: 6, lg: 2 }} mb="md">
                      <Card m="sm" style={{ cursor: 'pointer', boxShadow: cardShadow }} withBorder>
                        <Card.Section inheritPadding py="md" style={{ display: 'flex', flexDirection: 'column' }}>
                          <Text fw={500} mb="xs">
                            {asset.text}
                          </Text>
                          <div style={{ marginBottom: 'var(--mantine-spacing-xs)' }}>
                            <Select
                              data={TEXT_TYPE_OPTIONS}
                              value={asset.fieldType}
                              onChange={(value) => handleFieldTypeChange(asset, value)}
                            />
                          </div>
                          {invalid && (
                            <Stack gap={4} mb="xs">
                              <Text size="xs" c="red">
                                {validation.length} chars — limit is {validation.maxLength}.
                              </Text>
                              {suggested && canSwitch && (
                                <Button
                                  size="compact-xs"
                                  variant="light"
                                  onClick={() => handleFieldTypeChange(asset, suggested)}
                                >
                                  Switch to {suggestedConfig.label}
                                </Button>
                              )}
                              {suggested && !canSwitch && (
                                <Text size="xs" c="dimmed">
                                  Switch to {suggestedConfig.label} — limit reached
                                </Text>
                              )}
                            </Stack>
                          )}
                          <div style={{ marginTop: 'auto' }}>
                            <Text size="sm" c="dimmed" display="block">
                              {asset.name}
                            </Text>
                            <Button
                              size="sm"
                              w="100%"
                              mt="xs"
                              onClick={() => handleAssetToggle(asset, asset.fieldType)}
                            >
                              <IconCheck size={14} /> Selected
                            </Button>
                          </div>
                        </Card.Section>
                      </Card>
                    </Grid.Col>
                  );
                })}
              </Grid>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="images" p="md">
            {selectedImageAssets.length === 0 ? (
              <Text c="dimmed">No image assets selected</Text>
            ) : (
              <Grid gutter="lg" mt="md">
                {selectedImageAssets.map((asset) => {
                  const validation = ratioValidationByAsset.get(asset.resourceName);
                  const invalid = validation && validation.valid === false;
                  const suggested = invalid ? validation.suggestedFieldType : null;
                  const suggestedConfig = suggested ? IMAGE_ASSET_TYPES.find((t) => t.value === suggested) : null;
                  const canSwitch = suggested && canAddAsset(suggested);
                  const cardShadow = invalid
                    ? '0 0 0 2px var(--mantine-color-red-6)'
                    : '0 0 0 2px var(--mantine-primary-color-filled)';
                  return (
                    <Grid.Col key={asset.resourceName} span={{ base: 12, md: 6, lg: 2 }} mb="md">
                      <Card m="sm" style={{ cursor: 'pointer', boxShadow: cardShadow }} withBorder>
                        <Card.Section inheritPadding py="md" style={{ display: 'flex', flexDirection: 'column' }}>
                          {asset.url && (
                            <Container ta="center" p="xs">
                              <Image
                                src={asset.url}
                                alt={asset.name}
                                style={{ width: 'auto', height: 'auto', maxHeight: 150 }}
                                fit="contain"
                                referrerPolicy="no-referrer"
                              />
                            </Container>
                          )}
                          <Text fw={500} mb="xs">
                            {asset.name}
                          </Text>
                          <div style={{ marginBottom: 'var(--mantine-spacing-xs)' }}>
                            <Select
                              data={IMAGE_TYPE_OPTIONS}
                              value={asset.fieldType}
                              onChange={(value) => handleFieldTypeChange(asset, value)}
                            />
                          </div>
                          {invalid && (
                            <Stack gap={4} mb="xs">
                              <Text size="xs" c="red">
                                {validation.wrongRatio
                                  ? `${asset.width}×${asset.height} (${validation.actualRatioLabel}). Expected ${validation.expectedLabel} (${validation.recommended}).`
                                  : `${asset.width}×${asset.height} is below the ${validation.minWidth}×${validation.minHeight} minimum (recommended ${validation.recommended}).`}
                              </Text>
                              {suggested && canSwitch && (
                                <Button
                                  size="compact-xs"
                                  variant="light"
                                  onClick={() => handleFieldTypeChange(asset, suggested)}
                                >
                                  Switch to {suggestedConfig.label}
                                </Button>
                              )}
                              {suggested && !canSwitch && (
                                <Text size="xs" c="dimmed">
                                  Switch to {suggestedConfig.label} — limit reached
                                </Text>
                              )}
                            </Stack>
                          )}
                          <div style={{ marginTop: 'auto' }}>
                            {!invalid && (
                              <Text size="sm" c="dimmed" display="block">
                                Dimensions: {asset.width}x{asset.height}
                              </Text>
                            )}
                            <Button
                              size="sm"
                              w="100%"
                              mt="xs"
                              onClick={() => handleAssetToggle(asset, asset.fieldType)}
                            >
                              <IconCheck size={14} /> Selected
                            </Button>
                          </div>
                        </Card.Section>
                      </Card>
                    </Grid.Col>
                  );
                })}
              </Grid>
            )}
          </Tabs.Panel>
        </Tabs>
      </>
    );
  };

  const renderTextAssets = () => {
    if (loading) return <Spinner />;
    if (error) return <ErrorMessage message={error.message} />;
    if (!assetsLoaded.text) {
      return (
        <Box ta="center" style={{ marginTop: 'var(--mantine-spacing-md)' }}>
          <Button fullWidth onClick={() => loadAssets('text')}>
            <IconDots size={14} /> Load Text Assets
          </Button>
        </Box>
      );
    }

    return (
      <>
        <Grid mb="md">
          <Grid.Col span={12}>
            <Card withBorder>
              <Card.Section inheritPadding py="sm">
                <Grid>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="Filter by Text"
                      type="text"
                      placeholder="Enter asset text"
                      value={filterName}
                      onChange={(e) => setFilterName(e.target.value)}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 2 }}>
                    <Select
                      label="Filter Type"
                      data={FILTER_TYPES}
                      value={filterType}
                      onChange={(value) => setFilterType(value)}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 2 }} style={{ display: 'flex', alignItems: 'center' }} mt="md">
                    <Button w="100%" onClick={handleApplyFilter} disabled={loading}>
                      <IconFilter size={14} />
                      Apply Filter
                    </Button>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 2 }} style={{ display: 'flex', alignItems: 'center' }} mt="md">
                    <Button w="100%" variant="light" onClick={() => setAddAssetModalType('TEXT')}>
                      <IconPlus size={14} />
                      Add Asset
                    </Button>
                  </Grid.Col>
                </Grid>
              </Card.Section>
            </Card>
          </Grid.Col>
        </Grid>
        {textAssets.length === 0 ? (
          <Text c="dimmed">No text assets found in the Google Ads asset library</Text>
        ) : (
          <Grid gutter="lg" mt="md">
            {textAssets.map((asset) => {
              const selected = selectedAssets.find((a) => a.resourceName === asset.resourceName);
              const defaultFieldType = getDefaultFieldType(asset);
              const fieldType = selected?.fieldType || defaultFieldType;
              const validation = selected ? textValidationByAsset.get(asset.resourceName) : null;
              const showTooLongBadge = selected && validation && validation.valid === false;

              return (
                <Grid.Col key={asset.resourceName} span={{ base: 12, md: 6, lg: 2 }} mb="md">
                  <Card
                    m="sm"
                    style={{
                      cursor: 'pointer',
                      ...(selected ? { boxShadow: '0 0 0 2px var(--mantine-primary-color-filled)' } : {}),
                    }}
                    withBorder
                  >
                    <Card.Section inheritPadding py="md" style={{ display: 'flex', flexDirection: 'column' }}>
                      <Text fw={500} mb="xs">
                        {asset.text}
                      </Text>
                      <div style={{ marginBottom: 'var(--mantine-spacing-xs)' }}>
                        <Select
                          data={TEXT_TYPE_OPTIONS}
                          value={fieldType}
                          onChange={(value) => handleFieldTypeChange(asset, value)}
                        />
                      </div>
                      <div style={{ marginTop: 'auto' }}>
                        <Group gap={6} wrap="nowrap">
                          <Text size="sm" c="dimmed" display="block">
                            {asset.name} · {asset.text?.length ?? 0} chars
                          </Text>
                          {showTooLongBadge && (
                            <Badge color="red" size="xs">
                              Too long
                            </Badge>
                          )}
                        </Group>
                        <Button
                          variant={selected ? 'filled' : 'outline'}
                          size="sm"
                          w="100%"
                          mt="xs"
                          onClick={() => handleAssetToggle(asset, fieldType)}
                          disabled={!canAddAsset(fieldType) && !selected}
                        >
                          {selected ? (
                            <>
                              <IconCheck size={14} /> Selected
                            </>
                          ) : (
                            'Select'
                          )}
                        </Button>
                      </div>
                    </Card.Section>
                  </Card>
                </Grid.Col>
              );
            })}
          </Grid>
        )}
        {hasMore.text && (
          <Box ta="center" style={{ marginTop: 'var(--mantine-spacing-md)' }}>
            <Button fullWidth onClick={() => loadMoreAssets('text')} disabled={loading}>
              {loading ? <Loader size="sm" /> : 'Load More Text Assets'}
            </Button>
          </Box>
        )}
      </>
    );
  };

  const renderImageAssets = () => {
    if (loading) return <Spinner />;
    if (error) return <ErrorMessage message={error.message} />;
    if (!assetsLoaded.images) {
      return (
        <Box ta="center" style={{ marginTop: 'var(--mantine-spacing-md)' }}>
          <Button fullWidth onClick={() => loadAssets('images')}>
            <IconDots size={14} />
            Load Image Assets
          </Button>
        </Box>
      );
    }

    return (
      <>
        <Grid mb="md">
          <Grid.Col span={12}>
            <Card withBorder>
              <Card.Section inheritPadding py="sm">
                <Grid>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                      label="Filter by Name"
                      type="text"
                      placeholder="Enter asset name"
                      value={filterName}
                      onChange={(e) => setFilterName(e.target.value)}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 2 }}>
                    <Select
                      label="Filter Type"
                      data={FILTER_TYPES}
                      value={filterType}
                      onChange={(value) => setFilterType(value)}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 2 }} style={{ display: 'flex', alignItems: 'center' }} mt="md">
                    <Button w="100%" onClick={handleApplyFilter} disabled={loading}>
                      <IconFilter size={14} />
                      Apply Filter
                    </Button>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 2 }} style={{ display: 'flex', alignItems: 'center' }} mt="md">
                    <Button w="100%" variant="light" onClick={() => setAddAssetModalType('IMAGE')}>
                      <IconPlus size={14} />
                      Add Asset
                    </Button>
                  </Grid.Col>
                </Grid>
              </Card.Section>
            </Card>
          </Grid.Col>
        </Grid>
        {images.length === 0 ? (
          <Text c="dimmed">No image assets found in the Google Ads asset library</Text>
        ) : (
          <Grid gutter="lg" mt="md">
            {images.map((asset) => {
              const selected = selectedAssets.find((a) => a.resourceName === asset.resourceName);
              const defaultFieldType = getDefaultFieldType(asset);
              const fieldType = selected?.fieldType || defaultFieldType;
              const validation = selected ? ratioValidationByAsset.get(asset.resourceName) : null;
              const invalidSelected = selected && validation && validation.valid === false;

              return (
                <Grid.Col key={asset.resourceName} span={{ base: 12, md: 6, lg: 2 }} mb="md">
                  <Card
                    m="sm"
                    style={{
                      cursor: 'pointer',
                      ...(selected ? { boxShadow: '0 0 0 2px var(--mantine-primary-color-filled)' } : {}),
                    }}
                    withBorder
                  >
                    <Card.Section inheritPadding py="md" style={{ display: 'flex', flexDirection: 'column' }}>
                      <Container ta="center" p="xs">
                        <Image
                          src={asset.url}
                          alt={asset.name}
                          style={{ width: 'auto', height: 'auto', maxHeight: 150 }}
                          fit="contain"
                          referrerPolicy="no-referrer"
                        />
                      </Container>
                      <Text fw={500} mb="xs">
                        {asset.name}
                      </Text>
                      <div style={{ marginBottom: 'var(--mantine-spacing-xs)' }}>
                        <Select
                          data={IMAGE_TYPE_OPTIONS}
                          value={fieldType}
                          onChange={(value) => handleFieldTypeChange(asset, value)}
                        />
                      </div>
                      <Flex style={{ marginTop: 'auto' }} justify="space-between" align="center">
                        <Group gap={6} wrap="nowrap">
                          <Text size="sm" c="dimmed" display="block">
                            Dimensions: {asset.width}x{asset.height}
                          </Text>
                          {invalidSelected && (
                            <Badge color="red" size="xs">
                              {validation.wrongRatio ? 'Wrong ratio' : 'Too small'}
                            </Badge>
                          )}
                        </Group>
                        <Button
                          variant={selected ? 'filled' : 'outline'}
                          size="sm"
                          w="100%"
                          mt="xs"
                          onClick={() => handleAssetToggle(asset, fieldType)}
                          disabled={!canAddAsset(fieldType) && !selected}
                        >
                          {selected ? (
                            <>
                              <IconCheck size={14} /> Selected
                            </>
                          ) : (
                            'Select'
                          )}
                        </Button>
                      </Flex>
                    </Card.Section>
                  </Card>
                </Grid.Col>
              );
            })}
          </Grid>
        )}
        {hasMore.images && (
          <Box ta="center" style={{ marginTop: 'var(--mantine-spacing-md)' }}>
            <Button fullWidth onClick={() => loadMoreAssets('images')} disabled={loading}>
              {loading ? <Loader size="sm" /> : 'Load More Image Assets'}
            </Button>
          </Box>
        )}
      </>
    );
  };

  const handleAddAssetSuccess = useCallback(() => {
    const filterParams = filterName.trim() !== '' ? { filterName, filterType } : {};
    if (addAssetModalType === 'TEXT') {
      fetchAssets('text', textLimit, filterParams);
    } else if (addAssetModalType === 'IMAGE') {
      fetchAssets('images', imageLimit, filterParams);
    }
  }, [addAssetModalType, fetchAssets, textLimit, imageLimit, filterName, filterType]);

  return (
    <>
      <GoogleAddAssetsModal
        isOpen={addAssetModalType !== null}
        close={() => setAddAssetModalType(null)}
        defaultType={addAssetModalType || undefined}
        onSuccess={handleAddAssetSuccess}
      />
      <Card withBorder>
        <Card.Section inheritPadding py="sm">
          {renderSelectedAssets()}
        </Card.Section>
      </Card>
      <Card withBorder padding="md">
        <Flex align="center" justify="space-between" style={{ cursor: 'pointer' }} onClick={toggleOpen}>
          <Text fw={600} size="lg" span>
            Select from Assets Library
          </Text>
          <Button variant="subtle" size="compact-sm" component="span">
            {isOpen ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
          </Button>
        </Flex>
        {isOpen && (
          <Tabs value={activeTab} onChange={setActiveTab} mt="md">
            <Tabs.List>
              <Tabs.Tab value="text">Text Assets</Tabs.Tab>
              <Tabs.Tab value="images">Image Assets</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="text" p="md">
              {renderTextAssets()}
            </Tabs.Panel>
            <Tabs.Panel value="images" p="md">
              {renderImageAssets()}
            </Tabs.Panel>
          </Tabs>
        )}
      </Card>
    </>
  );
};

export default GoogleAssetSelector;
